/**
 * Image compression utility functions
 * Handles image resizing, format conversion, quality reduction and backward compatibility
 */
import { toast } from "@/hooks/use-toast";

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
  keepAspectRatio?: boolean;
  generateThumbnail?: boolean;
  thumbnailSize?: number;
  preserveOriginal?: boolean;
}

export interface CompressedImage {
  blob: Blob;
  originalSize: number;
  compressedSize: number;
  width: number;
  height: number;
  compressionRatio: number;
  format: string;
  thumbnail?: Blob;
  metadata?: ImageMetadata;
}

export interface ImageMetadata {
  originalWidth: number;
  originalHeight: number;
  originalFormat: string;
  originalUrl?: string;
  compressedUrl?: string;
  thumbnailUrl?: string;
  compressionTimestamp: number;
  compressionStats: string;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1200,
  maxHeight: 1200,
  quality: 0.85,
  format: 'webp',
  keepAspectRatio: true,
  generateThumbnail: false,
  thumbnailSize: 300,
  preserveOriginal: false
};

/**
 * Check if WebP format is supported by the browser
 */
export const isWebPSupported = (): boolean => {
  const canvas = document.createElement('canvas');
  if (!canvas || !canvas.getContext) {
    return false;
  }
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
};

/**
 * Extract image format from URL or data URL
 */
export const getImageFormatFromUrl = (url: string): string | null => {
  try {
    // Handle data URLs
    if (url.startsWith('data:image/')) {
      const format = url.substring(11, url.indexOf(';'));
      return format || null;
    }
    
    // Handle regular URLs with extensions
    const extension = url.split('.').pop()?.toLowerCase();
    if (extension) {
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'tiff'].includes(extension)) {
        return extension === 'jpg' ? 'jpeg' : extension;
      }
    }
    
    return null;
  } catch (e) {
    console.error('Error extracting image format:', e);
    return null;
  }
};

/**
 * Check if a URL is a data URL or a regular URL
 */
export const isDataUrl = (url: string): boolean => {
  return url.startsWith('data:');
};

/**
 * Fetch an image from a URL and return it as a Blob
 */
export const fetchImageAsBlob = async (url: string): Promise<Blob> => {
  try {
    // If it's already a data URL, convert it to Blob
    if (isDataUrl(url)) {
      const res = await fetch(url);
      return await res.blob();
    }
    
    // Otherwise fetch from remote URL
    const response = await fetch(url, { 
      mode: 'cors',
      cache: 'no-cache'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    
    return await response.blob();
  } catch (error) {
    console.error('Error fetching image:', error);
    throw error;
  }
};

/**
 * Convert a Blob to a File object
 */
export const blobToFile = (blob: Blob, fileName: string, fileLastModified?: number): File => {
  // Create a File from the Blob
  return new File([blob], fileName, { 
    type: blob.type, 
    lastModified: fileLastModified || Date.now() 
  });
};

/**
 * Compresses an image file to reduce its size while maintaining reasonable quality
 */
export const compressImage = async (
  fileOrBlob: File | Blob | string,
  options: CompressionOptions = {}
): Promise<CompressedImage> => {
  // Combine default options with provided options
  const settings = { ...DEFAULT_OPTIONS, ...options };
  
  // Use WebP if supported and not explicitly set to another format
  if (isWebPSupported() && !options.format) {
    settings.format = 'webp';
  }

  // Handle input types: string URL, File, or Blob
  let inputBlob: Blob;
  let originalFileName = 'image';
  let originalLastModified: number | undefined;
  
  if (typeof fileOrBlob === 'string') {
    // It's a URL or data URL
    inputBlob = await fetchImageAsBlob(fileOrBlob);
    const format = getImageFormatFromUrl(fileOrBlob) || 'jpg';
    originalFileName = `image.${format}`;
  } else if (fileOrBlob instanceof File) {
    // It's a File object
    inputBlob = fileOrBlob;
    originalFileName = fileOrBlob.name;
    originalLastModified = fileOrBlob.lastModified;
  } else {
    // It's a Blob
    inputBlob = fileOrBlob;
    // Try to determine a file extension from MIME type
    const extension = inputBlob.type.split('/')[1] || 'jpg';
    originalFileName = `image.${extension}`;
  }

  // Create a new promise to handle the image processing
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(inputBlob);
      
      reader.onload = (event) => {
        if (!event.target?.result) {
          reject(new Error('Failed to read image file'));
          return;
        }
        
        const img = new Image();
        img.src = event.target.result as string;
        
        img.onload = async () => {
          const originalWidth = img.width;
          const originalHeight = img.height;
          const originalFormat = inputBlob.type.split('/')[1] || 'jpeg';
          
          // Calculate new dimensions, respecting aspect ratio if required
          let { width, height } = calculateDimensions(
            img.width,
            img.height,
            settings.maxWidth!,
            settings.maxHeight!,
            settings.keepAspectRatio!
          );

          // Create canvas for image manipulation
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          // Draw image to canvas with new dimensions
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }
          
          // Use high-quality image rendering
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to specified format with quality setting
          const mimeType = `image/${settings.format}`;
          
          // Create thumbnail if requested
          let thumbnail: Blob | undefined;
          if (settings.generateThumbnail) {
            try {
              const thumbCanvas = document.createElement('canvas');
              const thumbSize = settings.thumbnailSize || 300;
              const thumbDimensions = calculateDimensions(
                originalWidth,
                originalHeight,
                thumbSize,
                thumbSize,
                true // Always keep aspect ratio for thumbnails
              );
              
              thumbCanvas.width = thumbDimensions.width;
              thumbCanvas.height = thumbDimensions.height;
              
              const thumbCtx = thumbCanvas.getContext('2d');
              if (thumbCtx) {
                thumbCtx.imageSmoothingQuality = 'high';
                thumbCtx.drawImage(img, 0, 0, thumbDimensions.width, thumbDimensions.height);
                
                thumbnail = await new Promise<Blob>((resolve, reject) => {
                  thumbCanvas.toBlob(
                    (blob) => {
                      if (blob) resolve(blob);
                      else reject(new Error('Failed to create thumbnail'));
                    },
                    mimeType,
                    0.7 // Lower quality for thumbnails
                  );
                });
              }
            } catch (thumbError) {
              console.warn('Error creating thumbnail:', thumbError);
              // Continue without thumbnail
            }
          }
          
          // Get compressed image as blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to generate compressed image'));
                return;
              }
              
              // Create metadata
              const metadata: ImageMetadata = {
                originalWidth,
                originalHeight,
                originalFormat,
                compressionTimestamp: Date.now(),
                compressionStats: `${(inputBlob.size / 1024).toFixed(1)}KB → ${(blob.size / 1024).toFixed(1)}KB (${Math.round((1 - blob.size / inputBlob.size) * 100)}% smaller)`
              };
              
              // If the input was a URL, store it in metadata
              if (typeof fileOrBlob === 'string' && !isDataUrl(fileOrBlob)) {
                metadata.originalUrl = fileOrBlob;
              }
              
              // Return compression results
              const result: CompressedImage = {
                blob,
                originalSize: inputBlob.size,
                compressedSize: blob.size,
                width,
                height,
                compressionRatio: (inputBlob.size / blob.size).toFixed(2) as unknown as number,
                format: settings.format!,
                thumbnail,
                metadata
              };
              
              resolve(result);
            },
            mimeType,
            settings.quality
          );
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
    } catch (error) {
      console.error('Image compression error:', error);
      reject(error);
    }
  });
};

/**
 * Creates a thumbnail version of an image
 */
export const createThumbnail = async (
  fileOrBlob: File | Blob | string,
  maxSize: number = 300
): Promise<Blob> => {
  return compressImage(fileOrBlob, {
    maxWidth: maxSize,
    maxHeight: maxSize,
    quality: 0.8,
    format: 'webp',
    generateThumbnail: false,
  }).then(result => result.blob);
};

/**
 * Calculates new dimensions while optionally maintaining aspect ratio
 */
const calculateDimensions = (
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  maxHeight: number,
  keepAspectRatio: boolean
): { width: number; height: number } => {
  if (keepAspectRatio) {
    // Calculate dimensions preserving aspect ratio
    if (srcWidth > srcHeight) {
      // Landscape orientation
      if (srcWidth > maxWidth) {
        const ratio = maxWidth / srcWidth;
        return {
          width: maxWidth,
          height: Math.round(srcHeight * ratio)
        };
      }
    } else {
      // Portrait orientation
      if (srcHeight > maxHeight) {
        const ratio = maxHeight / srcHeight;
        return {
          width: Math.round(srcWidth * ratio),
          height: maxHeight
        };
      }
    }
    // If image is already smaller than the max dimensions, keep original size
    return { width: srcWidth, height: srcHeight };
  } else {
    // Force resize to max dimensions regardless of aspect ratio
    return {
      width: Math.min(srcWidth, maxWidth),
      height: Math.min(srcHeight, maxHeight)
    };
  }
};

/**
 * Formats the file size in a human-readable form
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get compression statistics in a human-readable format
 */
export const getCompressionStats = (result: CompressedImage): string => {
  const originalSize = formatFileSize(result.originalSize);
  const compressedSize = formatFileSize(result.compressedSize);
  const savedPercentage = Math.round((1 - (result.compressedSize / result.originalSize)) * 100);
  
  return `Reduced from ${originalSize} to ${compressedSize} (${savedPercentage}% smaller)`;
};

/**
 * Process an existing image URL - fetch, compress and return as blob or data URL
 */
export const processExistingImage = async (
  imageUrl: string,
  options: CompressionOptions = {}
): Promise<{ blob: Blob; dataUrl?: string; stats: string }> => {
  try {
    // Compress the image
    const compressed = await compressImage(imageUrl, options);
    
    // Get statistics
    const stats = getCompressionStats(compressed);
    
    // Create data URL if needed
    let dataUrl: string | undefined;
    if (options.preserveOriginal) {
      const reader = new FileReader();
      dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(compressed.blob);
      });
    }
    
    return {
      blob: compressed.blob,
      dataUrl,
      stats
    };
  } catch (error) {
    console.error('Error processing existing image:', error);
    throw error;
  }
};

/**
 * Helper function to handle common image compression with toast notification
 */
export const compressAndNotify = async (
  file: File | Blob | string,
  options?: CompressionOptions
): Promise<Blob | null> => {
  try {
    const compressed = await compressImage(file, options);
    
    // Only show a toast if compression saved significant space (>10%)
    if (compressed.compressedSize < compressed.originalSize * 0.9) {
      const stats = getCompressionStats(compressed);
      toast({
        title: "Image optimized",
        description: stats,
        duration: 3000,
      });
    }
    
    return compressed.blob;
  } catch (error) {
    console.error('Image compression failed:', error);
    return null;
  }
};

/**
 * Generate an optimized version of an image from a storage URL
 * This is useful for batch processing existing images
 */
export const optimizeStorageImage = async (
  imageUrl: string,
  onSuccess?: (result: CompressedImage) => void,
  onError?: (error: Error) => void
): Promise<CompressedImage | null> => {
  try {
    // Default options for optimization
    const options: CompressionOptions = {
      maxWidth: 1600,
      maxHeight: 1600,
      quality: 0.85,
      format: 'webp',
      generateThumbnail: true,
      thumbnailSize: 400
    };
    
    // Compress the image
    const result = await compressImage(imageUrl, options);
    
    if (onSuccess) {
      onSuccess(result);
    }
    
    return result;
  } catch (error) {
    console.error('Failed to optimize storage image:', error);
    
    if (onError && error instanceof Error) {
      onError(error);
    }
    
    return null;
  }
};

/**
 * Create a worker for batch processing images
 */
export const createBatchProcessor = async (
  imageUrls: string[],
  progressCallback: (progress: number, total: number) => void,
  resultCallback: (results: { url: string; compressed: CompressedImage | null; error?: Error }[]) => void
) => {
  const results: { url: string; compressed: CompressedImage | null; error?: Error }[] = [];
  let processed = 0;
  
  // Process in batches of 3 to avoid overwhelming the browser
  const batchSize = 3;
  const total = imageUrls.length;
  
  for (let i = 0; i < total; i += batchSize) {
    const batch = imageUrls.slice(i, i + batchSize);
    
    // Process this batch in parallel
    const batchPromises = batch.map(async (url) => {
      try {
        const result = await optimizeStorageImage(url);
        results.push({ url, compressed: result });
      } catch (error) {
        if (error instanceof Error) {
          results.push({ url, compressed: null, error });
        } else {
          results.push({ url, compressed: null, error: new Error('Unknown error') });
        }
      }
      
      processed++;
      progressCallback(processed, total);
    });
    
    // Wait for this batch to complete before starting the next one
    await Promise.all(batchPromises);
  }
  
  // All batches completed
  resultCallback(results);
};
